import { useMemo, useState } from 'react'
import {
  Box, Breadcrumbs, Typography, Stack, Button, Chip, IconButton, Menu, MenuItem, ListItemIcon, ListItemText, Snackbar, Alert,
} from '@mui/material'
import { Add, MoreVert, Edit, History, Send, Description, ShoppingCartCheckout, RuleFolder } from '@mui/icons-material'
import { DataGrid, type GridColDef } from '@mui/x-data-grid'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  listOffers, createOfferRevision, submitOfferForApproval, sendOffer, getOfferDocumentHtml,
  type OfferListItem, type OfferStatus,
} from '../../api/oferte'
import { errorMessage } from '../../api/client'
import OfferFormDialog from './OfferFormDialog'
import OfferHistoryDialog from './OfferHistoryDialog'
import ConvertToOrderDialog from './ConvertToOrderDialog'

const STATUS_LABEL: Record<OfferStatus, string> = {
  Draft: 'Ciornă', PendingApproval: 'În aprobare', Approved: 'Aprobată', Sent: 'Trimisă',
  Expired: 'Expirată', Converted: 'Convertită', Rejected: 'Respinsă',
}
const STATUS_COLOR: Record<OfferStatus, 'default' | 'success' | 'warning' | 'info' | 'error'> = {
  Draft: 'default', PendingApproval: 'warning', Approved: 'success', Sent: 'info',
  Expired: 'error', Converted: 'success', Rejected: 'error',
}

export default function OfertePage() {
  const [formTarget, setFormTarget] = useState<OfferListItem | null | 'new'>(null)
  const [historyTarget, setHistoryTarget] = useState<string | null>(null)
  const [convertTarget, setConvertTarget] = useState<OfferListItem | null>(null)
  const [menuAnchor, setMenuAnchor] = useState<{ el: HTMLElement; row: OfferListItem } | null>(null)
  const [error, setError] = useState<string | null>(null)
  const qc = useQueryClient()
  const { data, isLoading } = useQuery({ queryKey: ['comercial', 'oferte'], queryFn: listOffers })
  const closeMenu = () => setMenuAnchor(null)

  const invalidate = () => void qc.invalidateQueries({ queryKey: ['comercial', 'oferte'] })

  const revisionMutation = useMutation({ mutationFn: createOfferRevision, onSuccess: invalidate, onError: (e) => setError(errorMessage(e)) })
  const submitMutation = useMutation({
    mutationFn: submitOfferForApproval,
    onSuccess: (r) => { invalidate(); setError(r.pendingApproval ? 'Ofertă trimisă la aprobare.' : null) },
    onError: (e) => setError(errorMessage(e)),
  })
  const sendMutation = useMutation({ mutationFn: sendOffer, onSuccess: invalidate, onError: (e) => setError(errorMessage(e)) })

  const viewDocument = async (id: string) => {
    try {
      const html = await getOfferDocumentHtml(id)
      const win = window.open('', '_blank')
      if (win) { win.document.write(html); win.document.close() }
    } catch (e) {
      setError(errorMessage(e))
    }
  }

  const columns = useMemo<GridColDef<OfferListItem>[]>(() => [
    { field: 'offerNumber', headerName: 'Nr.', width: 130, renderCell: (p) => <code>{p.row.offerNumber} v{p.row.versionNumber}</code> },
    { field: 'offerDate', headerName: 'Data', width: 110 },
    { field: 'clientName', headerName: 'Client', flex: 1, minWidth: 180 },
    { field: 'agentUserName', headerName: 'Agent', width: 150 },
    {
      field: 'status', headerName: 'Status', width: 220, sortable: false,
      renderCell: (p) => (
        <Stack direction="row" spacing={0.5}>
          <Chip size="small" label={STATUS_LABEL[p.row.status]} color={STATUS_COLOR[p.row.status]} variant="outlined" />
          {p.row.isExpired && <Chip size="small" label="Expirată" color="error" />}
          {!p.row.isExpired && p.row.isExpiringSoon && <Chip size="small" label="Expiră curând" color="warning" />}
        </Stack>
      ),
    },
    { field: 'validUntil', headerName: 'Valabilă până la', width: 130 },
    { field: 'total', headerName: 'Total', width: 110, type: 'number', valueFormatter: (v: number) => v.toFixed(2).replace('.', ',') },
    {
      field: 'actions', headerName: '', width: 56, sortable: false, filterable: false, disableColumnMenu: true,
      renderCell: (p) => (
        <IconButton size="small" onClick={(e) => setMenuAnchor({ el: e.currentTarget, row: p.row })}>
          <MoreVert fontSize="small" />
        </IconButton>
      ),
    },
  ], [])

  return (
    <Stack spacing={2}>
      <Breadcrumbs separator="/" sx={{ fontSize: 12.5 }}>
        <Typography variant="inherit" color="text.secondary">Comercial</Typography>
        <Typography variant="inherit" color="text.primary">Oferte</Typography>
      </Breadcrumbs>
      <Typography variant="h2">Oferte</Typography>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="contained" startIcon={<Add />} onClick={() => setFormTarget('new')}>Ofertă nouă</Button>
      </Box>
      <Box sx={{ height: 560, bgcolor: 'background.paper', borderRadius: 1.5 }}>
        <DataGrid rows={data ?? []} columns={columns} loading={isLoading} density="comfortable" disableRowSelectionOnClick
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }} pageSizeOptions={[25, 50]} />
      </Box>

      <OfferFormDialog target={formTarget} onClose={() => setFormTarget(null)} />
      <OfferHistoryDialog offerNumber={historyTarget} onClose={() => setHistoryTarget(null)} />
      <ConvertToOrderDialog offer={convertTarget} onClose={() => setConvertTarget(null)} onConverted={invalidate} />

      <Menu anchorEl={menuAnchor?.el} open={!!menuAnchor} onClose={closeMenu}>
        {menuAnchor && [
          <MenuItem key="edit" disabled={menuAnchor.row.status !== 'Draft'} onClick={() => { setFormTarget(menuAnchor.row); closeMenu() }}>
            <ListItemIcon><Edit fontSize="small" /></ListItemIcon><ListItemText>Editare</ListItemText>
          </MenuItem>,
          <MenuItem key="submit" disabled={menuAnchor.row.status !== 'Draft'}
            onClick={() => { submitMutation.mutate(menuAnchor.row.id); closeMenu() }}>
            <ListItemIcon><RuleFolder fontSize="small" /></ListItemIcon><ListItemText>Trimite la aprobare</ListItemText>
          </MenuItem>,
          <MenuItem key="send" disabled={!['Draft', 'Approved'].includes(menuAnchor.row.status)}
            onClick={() => { sendMutation.mutate(menuAnchor.row.id); closeMenu() }}>
            <ListItemIcon><Send fontSize="small" /></ListItemIcon><ListItemText>Trimite pe email</ListItemText>
          </MenuItem>,
          <MenuItem key="document" onClick={() => { void viewDocument(menuAnchor.row.id); closeMenu() }}>
            <ListItemIcon><Description fontSize="small" /></ListItemIcon><ListItemText>Vezi documentul</ListItemText>
          </MenuItem>,
          <MenuItem key="revizie" disabled={!['Draft', 'Sent', 'Approved', 'Rejected'].includes(menuAnchor.row.status)}
            onClick={() => { revisionMutation.mutate(menuAnchor.row.id); closeMenu() }}>
            <ListItemIcon><History fontSize="small" /></ListItemIcon><ListItemText>Revizie nouă</ListItemText>
          </MenuItem>,
          <MenuItem key="istoric" onClick={() => { setHistoryTarget(menuAnchor.row.offerNumber); closeMenu() }}>
            <ListItemIcon><History fontSize="small" /></ListItemIcon><ListItemText>Istoric versiuni</ListItemText>
          </MenuItem>,
          <MenuItem key="convert" disabled={!['Approved', 'Sent'].includes(menuAnchor.row.status)}
            onClick={() => { setConvertTarget(menuAnchor.row); closeMenu() }}>
            <ListItemIcon><ShoppingCartCheckout fontSize="small" /></ListItemIcon><ListItemText>Convertește în comandă</ListItemText>
          </MenuItem>,
        ]}
      </Menu>

      <Snackbar open={!!error} autoHideDuration={5000} onClose={() => setError(null)}>
        <Alert severity={error?.includes('aprobare') ? 'info' : 'error'} onClose={() => setError(null)}>{error}</Alert>
      </Snackbar>
    </Stack>
  )
}
